import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

// Layouts are part of the shell — keep them eager so the frame paints instantly.
import RootLayout from './pages/_layout';
import PaymentLayout from './pages/payment/_layout';
import { AuthProvider } from './contexts/AuthContext';
import { RequireAuth } from './components/auth/RequireAuth';

/** Full-viewport fallback shown while a route chunk loads. */
function RouteFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen text-[#999999]">
      <Loader2 size={28} className="animate-spin" />
    </div>
  );
}

// Marketing & auth
const LandingPage = lazy(() => import('./pages/marketing/LandingPage'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const SignupPage = lazy(() => import('./pages/auth/SignupPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage'));
const ChangePasswordPage = lazy(() => import('./pages/auth/ChangePasswordPage'));
const ServicesPage = lazy(() => import('./pages/marketing/ServicesPage'));
const FeaturesPage = lazy(() => import('./pages/marketing/FeaturesPage'));
const SecurityPage = lazy(() => import('./pages/marketing/SecurityPage'));
const AboutUsPage = lazy(() => import('./pages/marketing/AboutUsPage'));
const CareersPage = lazy(() => import('./pages/marketing/CareersPage'));
const PressPage = lazy(() => import('./pages/marketing/PressPage'));
const FAQPage = lazy(() => import('./pages/marketing/FAQPage'));
const ContactPage = lazy(() => import('./pages/marketing/ContactPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/marketing/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/marketing/TermsOfServicePage'));
const CookiePolicyPage = lazy(() => import('./pages/marketing/CookiePolicyPage'));

// Payment — dashboard
const PaymentDashboard = lazy(() => import('./pages/payment/index'));

// Send flow
const SendStart = lazy(() => import('./pages/payment/send/index'));
const ChooseRecipientType = lazy(() => import('./pages/payment/send/recipient/index'));
const SendToUser = lazy(() => import('./pages/payment/send/recipient/user'));
const SendToCompany = lazy(() => import('./pages/payment/send/recipient/company'));
const SendToCareer = lazy(() => import('./pages/payment/send/recipient/career'));
const SendExternal = lazy(() => import('./pages/payment/send/recipient/external'));
const SearchRecipients = lazy(() => import('./pages/payment/send/recipient/search'));
const EnterAmount = lazy(() => import('./pages/payment/send/amount/index'));
const SelectCurrency = lazy(() => import('./pages/payment/send/amount/currency'));
const ExchangeRateView = lazy(() => import('./pages/payment/send/amount/exchange'));
const SplitPayment = lazy(() => import('./pages/payment/send/amount/split'));
const ChooseMethod = lazy(() => import('./pages/payment/send/method/index'));
const BankTransfer = lazy(() => import('./pages/payment/send/method/bank'));
const CardPayment = lazy(() => import('./pages/payment/send/method/card'));
const WalletPayment = lazy(() => import('./pages/payment/send/method/wallet'));
const CryptoPayment = lazy(() => import('./pages/payment/send/method/crypto'));
const QrPayment = lazy(() => import('./pages/payment/send/method/qr'));
const ReviewIndex = lazy(() => import('./pages/payment/send/review/index'));
const FeeBreakdown = lazy(() => import('./pages/payment/send/review/fees'));
const Confirmation = lazy(() => import('./pages/payment/send/review/confirmation'));
const Processing = lazy(() => import('./pages/payment/send/processing/index'));
const ProcessingStatus = lazy(() => import('./pages/payment/send/processing/status'));
const ProcessingTimeout = lazy(() => import('./pages/payment/send/processing/timeout'));
const SendSuccess = lazy(() => import('./pages/payment/send/complete/success'));
const SendFailed = lazy(() => import('./pages/payment/send/complete/failed'));
const ReceiptPage = lazy(() => import('./pages/payment/send/complete/receipt'));
const SharePage = lazy(() => import('./pages/payment/send/complete/share'));

// Receive
const ReceiveDashboard = lazy(() => import('./pages/payment/receive/index'));
const QrGeneratorPage = lazy(() => import('./pages/payment/receive/qr-generator'));
const BarcodePage = lazy(() => import('./pages/payment/receive/barcode'));
const PaymentLinkPage = lazy(() => import('./pages/payment/receive/payment-link'));
const InvoicesPage = lazy(() => import('./pages/payment/receive/invoices'));
const RequestsPage = lazy(() => import('./pages/payment/receive/requests'));

// Scan
const ScannerHome = lazy(() => import('./pages/payment/scan/index'));
const QrScannerPage = lazy(() => import('./pages/payment/scan/qr-scanner'));
const BarcodeScannerPage = lazy(() => import('./pages/payment/scan/barcode-scanner'));
const ScanResultPage = lazy(() => import('./pages/payment/scan/result'));
const ScanHistoryPage = lazy(() => import('./pages/payment/scan/history'));

// Services
const ServicesDashboard = lazy(() => import('./pages/payment/services/index'));
const CareerServices = lazy(() => import('./pages/payment/services/career/index'));
const ConsultingPayment = lazy(() => import('./pages/payment/services/career/consulting'));
const MentoringPayment = lazy(() => import('./pages/payment/services/career/mentoring'));
const TrainingPayment = lazy(() => import('./pages/payment/services/career/training'));
const FreelancePayment = lazy(() => import('./pages/payment/services/career/freelance'));
const CompanyServices = lazy(() => import('./pages/payment/services/company/index'));
const CompanyProducts = lazy(() => import('./pages/payment/services/company/products'));
const Subscriptions = lazy(() => import('./pages/payment/services/company/subscriptions'));
const CompanyInvoices = lazy(() => import('./pages/payment/services/company/invoices'));
const BulkPayroll = lazy(() => import('./pages/payment/services/company/bulk'));
const EscrowDashboard = lazy(() => import('./pages/payment/services/escrow/index'));
const CreateEscrow = lazy(() => import('./pages/payment/services/escrow/create'));
const ManageEscrow = lazy(() => import('./pages/payment/services/escrow/manage'));
const ReleaseEscrow = lazy(() => import('./pages/payment/services/escrow/release'));

// International
const InternationalDashboard = lazy(() => import('./pages/payment/international/index'));
const CountriesPage = lazy(() => import('./pages/payment/international/countries'));
const ExchangeRatesPage = lazy(() => import('./pages/payment/international/exchange-rates'));
const InternationalFeesPage = lazy(() => import('./pages/payment/international/fees'));
const CompliancePage = lazy(() => import('./pages/payment/international/compliance'));
const TrackingPage = lazy(() => import('./pages/payment/international/tracking'));

// Wallets
const WalletsDashboard = lazy(() => import('./pages/payment/wallets/index'));
const MainWallet = lazy(() => import('./pages/payment/wallets/main'));
const CurrencyWallets = lazy(() => import('./pages/payment/wallets/currency-wallets'));
const CryptoWallets = lazy(() => import('./pages/payment/wallets/crypto-wallets'));
const WalletTransferPage = lazy(() => import('./pages/payment/wallets/transfer'));
const WalletSecurity = lazy(() => import('./pages/payment/wallets/security'));

// History
const TransactionHistory = lazy(() => import('./pages/payment/history/index'));
const SentPayments = lazy(() => import('./pages/payment/history/sent'));
const ReceivedPayments = lazy(() => import('./pages/payment/history/received'));
const PendingTransactions = lazy(() => import('./pages/payment/history/pending'));
const Disputes = lazy(() => import('./pages/payment/history/disputes'));
const ExportHistory = lazy(() => import('./pages/payment/history/exports'));

// Security
const SecurityDashboard = lazy(() => import('./pages/payment/security/index'));
const TransactionPin = lazy(() => import('./pages/payment/security/pin'));
const Biometrics = lazy(() => import('./pages/payment/security/biometrics'));
const PaymentLimits = lazy(() => import('./pages/payment/security/limits'));
const TwoFactor = lazy(() => import('./pages/payment/security/2fa'));
const TrustedDevices = lazy(() => import('./pages/payment/security/devices'));

// Support
const SupportHub = lazy(() => import('./pages/payment/support/index'));
const FileDispute = lazy(() => import('./pages/payment/support/disputes'));
const Chargebacks = lazy(() => import('./pages/payment/support/chargebacks'));
const Refunds = lazy(() => import('./pages/payment/support/refunds'));
const ContactSupport = lazy(() => import('./pages/payment/support/contact'));

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
          {/* Public marketing + auth */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/about" element={<AboutUsPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/press" element={<PressPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/cookies" element={<CookiePolicyPage />} />

          {/* Authenticated app — RequireAuth gates on sign-in + email verification */}
          <Route element={<RequireAuth />}>
          <Route element={<RootLayout />}>
            <Route path="/payment" element={<PaymentLayout />}>
              <Route index element={<PaymentDashboard />} />

              {/* Send */}
              <Route path="send" element={<SendStart />} />
              <Route path="send/recipient" element={<ChooseRecipientType />} />
              <Route path="send/recipient/user" element={<SendToUser />} />
              <Route path="send/recipient/company" element={<SendToCompany />} />
              <Route path="send/recipient/career" element={<SendToCareer />} />
              <Route path="send/recipient/external" element={<SendExternal />} />
              <Route path="send/recipient/search" element={<SearchRecipients />} />
              <Route path="send/amount" element={<EnterAmount />} />
              <Route path="send/amount/currency" element={<SelectCurrency />} />
              <Route path="send/amount/exchange" element={<ExchangeRateView />} />
              <Route path="send/amount/split" element={<SplitPayment />} />
              <Route path="send/method" element={<ChooseMethod />} />
              <Route path="send/method/bank" element={<BankTransfer />} />
              <Route path="send/method/card" element={<CardPayment />} />
              <Route path="send/method/wallet" element={<WalletPayment />} />
              <Route path="send/method/crypto" element={<CryptoPayment />} />
              <Route path="send/method/qr" element={<QrPayment />} />
              <Route path="send/review" element={<ReviewIndex />} />
              <Route path="send/review/fees" element={<FeeBreakdown />} />
              <Route path="send/review/confirmation" element={<Confirmation />} />
              <Route path="send/processing" element={<Processing />} />
              <Route path="send/processing/status" element={<ProcessingStatus />} />
              <Route path="send/processing/timeout" element={<ProcessingTimeout />} />
              <Route path="send/complete/success" element={<SendSuccess />} />
              <Route path="send/complete/failed" element={<SendFailed />} />
              <Route path="send/complete/receipt" element={<ReceiptPage />} />
              <Route path="send/complete/share" element={<SharePage />} />

              {/* Receive */}
              <Route path="receive" element={<ReceiveDashboard />} />
              <Route path="receive/qr-generator" element={<QrGeneratorPage />} />
              <Route path="receive/barcode" element={<BarcodePage />} />
              <Route path="receive/payment-link" element={<PaymentLinkPage />} />
              <Route path="receive/invoices" element={<InvoicesPage />} />
              <Route path="receive/requests" element={<RequestsPage />} />

              {/* Scan */}
              <Route path="scan" element={<ScannerHome />} />
              <Route path="scan/qr-scanner" element={<QrScannerPage />} />
              <Route path="scan/barcode-scanner" element={<BarcodeScannerPage />} />
              <Route path="scan/result" element={<ScanResultPage />} />
              <Route path="scan/history" element={<ScanHistoryPage />} />

              {/* Services */}
              <Route path="services" element={<ServicesDashboard />} />
              <Route path="services/career" element={<CareerServices />} />
              <Route path="services/career/consulting" element={<ConsultingPayment />} />
              <Route path="services/career/mentoring" element={<MentoringPayment />} />
              <Route path="services/career/training" element={<TrainingPayment />} />
              <Route path="services/career/freelance" element={<FreelancePayment />} />
              <Route path="services/company" element={<CompanyServices />} />
              <Route path="services/company/products" element={<CompanyProducts />} />
              <Route path="services/company/subscriptions" element={<Subscriptions />} />
              <Route path="services/company/invoices" element={<CompanyInvoices />} />
              <Route path="services/company/bulk" element={<BulkPayroll />} />
              <Route path="services/escrow" element={<EscrowDashboard />} />
              <Route path="services/escrow/create" element={<CreateEscrow />} />
              <Route path="services/escrow/manage" element={<ManageEscrow />} />
              <Route path="services/escrow/release" element={<ReleaseEscrow />} />

              {/* International */}
              <Route path="international" element={<InternationalDashboard />} />
              <Route path="international/countries" element={<CountriesPage />} />
              <Route path="international/exchange-rates" element={<ExchangeRatesPage />} />
              <Route path="international/fees" element={<InternationalFeesPage />} />
              <Route path="international/compliance" element={<CompliancePage />} />
              <Route path="international/tracking" element={<TrackingPage />} />

              {/* Wallets */}
              <Route path="wallets" element={<WalletsDashboard />} />
              <Route path="wallets/main" element={<MainWallet />} />
              <Route path="wallets/currency-wallets" element={<CurrencyWallets />} />
              <Route path="wallets/crypto-wallets" element={<CryptoWallets />} />
              <Route path="wallets/transfer" element={<WalletTransferPage />} />
              <Route path="wallets/security" element={<WalletSecurity />} />

              {/* History */}
              <Route path="history" element={<TransactionHistory />} />
              <Route path="history/sent" element={<SentPayments />} />
              <Route path="history/received" element={<ReceivedPayments />} />
              <Route path="history/pending" element={<PendingTransactions />} />
              <Route path="history/disputes" element={<Disputes />} />
              <Route path="history/exports" element={<ExportHistory />} />

              {/* Security */}
              <Route path="security" element={<SecurityDashboard />} />
              <Route path="security/pin" element={<TransactionPin />} />
              <Route path="security/biometrics" element={<Biometrics />} />
              <Route path="security/limits" element={<PaymentLimits />} />
              <Route path="security/2fa" element={<TwoFactor />} />
              <Route path="security/devices" element={<TrustedDevices />} />
              <Route path="security/password" element={<ChangePasswordPage />} />

              {/* Support */}
              <Route path="support" element={<SupportHub />} />
              <Route path="support/disputes" element={<FileDispute />} />
              <Route path="support/chargebacks" element={<Chargebacks />} />
              <Route path="support/refunds" element={<Refunds />} />
              <Route path="support/contact" element={<ContactSupport />} />
            </Route>
          </Route>
          </Route>

          <Route path="*" element={<Navigate to="/payment" replace />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}
